import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowRight } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import TestCard from './TestCard';
import { AllTestsListSkeleton } from './AllTestsListSkeleton';
import { usePackStorage } from '../../Layout/usePackStorage';

export default function AllTestsList({ packId }) {
  const { freePacks } = usePackStorage();
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const [freemiumCategories, setFreemiumCategories] = useState(new Set());
  const scrollContainerRef = useRef(null);
  let navigate = useNavigate();

  useEffect(() => {
    const fetchAllTests = async () => {
      setLoading(true);
      setError(null);
      if (packId) {
        try {
          const response = await axios.get(`/api/packs/${packId}/allTests`);
          if (freePacks?.length) {
            const categories = response?.data?.data;
            const freemiumCategoriesSet = new Set();
            const reordered = Object.fromEntries(
              Object.entries(categories).sort(([keyA, a], [keyB, b]) => {
                const aHasFreemium = a.some((item) => item.isFreemium);
                const bHasFreemium = b.some((item) => item.isFreemium);
                if (aHasFreemium) freemiumCategoriesSet.add(keyA);
                if (bHasFreemium) freemiumCategoriesSet.add(keyB);
                return bHasFreemium - aHasFreemium;
              }),
            );
            setFreemiumCategories(freemiumCategoriesSet);
            response.data.data = reordered;
          }
          setApiData(response.data);
        } catch (err) {
          console.error('API Error:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAllTests();
  }, [packId, freePacks]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;

    if (!isScrollable) {
      setShowLeftShadow(false);
      setShowRightShadow(false);
      return;
    }

    setShowLeftShadow(scrollLeft > 0);
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      handleScroll();

      container.addEventListener('scroll', handleScroll);

      const resizeObserver = new ResizeObserver(handleScroll);
      resizeObserver.observe(container);

      return () => {
        container.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [apiData]);

  if (loading) {
    return <AllTestsListSkeleton />;
  }
  return (
    <main className=" md:border-none py-3 px-4 md:px-2">
      <div className="flex w-full flex-col space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">
            <span className="text-[var(--q3-neutral-default)]">All Tests</span>
          </h1>
          <Button
            variant="outline"
            onClick={() => navigate(`/v3/${packId}/tests`)}
            className="shadow-none border-0 md:border !bg-[var(--q3-surface-default)]"
          >
            <span className="hidden sm:inline text-[var(--q3-accent-normal)] font-semibold">View All Tests</span>
            <span className="inline sm:hidden text-[var(--q3-accent-normal)]">View All</span>
            <ArrowRight className="hidden sm:inline h-4 w-4 ml-1 text-[var(--q3-accent-normal)]" />
          </Button>
        </div>
        <div className="relative">
          {showLeftShadow && (
            <div className="absolute left-0 top-0 bottom-0 w-8 z-3 pointer-events-none [background:linear-gradient(to_right,var(--q3-neutral-default_inverted),transparent)]" />
          )}

          {showRightShadow && (
            <div className="absolute right-0 top-0 bottom-0 w-8 z-3 pointer-events-none [background:linear-gradient(to_left,var(--q3-neutral-default_inverted),transparent)]" />
          )}
          <div className="flex flex-col gap-8 mt-6">
            {apiData &&
              apiData?.data &&
              Object?.keys(apiData?.data)?.map((key) => {
                return (
                  <div className="flex flex-col gap-4" key={key}>
                    <div className="flex items-center gap-2 px-2 mb-2">
                      <p className="text-sm text-[var(--q3-neutral-light)] font-medium tracking-wider whitespace-nowrap">
                        {key}
                      </p>
                      {freemiumCategories.has(key) && (
                        <Badge className="text-[var(--q3-neutral-default_inverted)] bg-[var(--q3-danger-normal)]">
                          Free
                        </Badge>
                      )}
                      <div className="flex-1 h-[1px] bg-gradient-to-r from-[var(--q3-stroke-normal)] to-[var(--q3-surface-default)]" />
                    </div>
                    <div
                      ref={scrollContainerRef}
                      className="grid grid-cols-1 gap-1 md:gap-6 md:grid-cols-3"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                      {Array.isArray(apiData?.data[key]) &&
                        apiData.data[key].map((test, index) => (
                          <div key={test.id || index} className="md:flex-shrink-0 w-full py-3">
                            <TestCard test={test} packId={packId} />
                          </div>
                        ))}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </main>
  );
}
